import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { toast } from 'sonner';
import { Shield, Download, Trash2, FileText, Lock, Eye } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PrivacySettings = ({ user, onBack }) => {
  const [privacySettings, setPrivacySettings] = useState({
    data_collection: true,
    personalization: true,
    analytics: true,
    third_party_sharing: false,
    marketing_emails: false
  });
  const [consents, setConsents] = useState([]);
  const [auditLogs, setAuditLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [exportData, setExportData] = useState(null);

  useEffect(() => {
    fetchPrivacySettings();
    fetchConsents();
    fetchAuditLogs();
  }, []);

  const fetchPrivacySettings = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/compliance/privacy-settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPrivacySettings(response.data);
    } catch (error) {
      console.error('Failed to fetch privacy settings', error);
    }
  };

  const fetchConsents = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/compliance/consent`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setConsents(response.data.consents || []);
    } catch (error) {
      console.error('Failed to fetch consents', error);
    }
  };

  const fetchAuditLogs = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/compliance/audit-log`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAuditLogs(response.data.audit_logs || []);
    } catch (error) {
      console.error('Failed to fetch audit logs', error);
    }
  };

  const handleUpdatePrivacySettings = async (key, value) => {
    const updatedSettings = { ...privacySettings, [key]: value };
    setPrivacySettings(updatedSettings);

    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/compliance/privacy-settings`, updatedSettings, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Privacy settings updated successfully!');
    } catch (error) {
      toast.error('Failed to update privacy settings');
      // Revert on error
      setPrivacySettings(privacySettings);
    }
  };

  const handleExportData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/compliance/data-export`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setExportData(response.data);
      
      // Download as JSON
      const dataStr = JSON.stringify(response.data, null, 2);
      const dataBlob = new Blob([dataStr], { type: 'application/json' });
      const url = URL.createObjectURL(dataBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `my-data-export-${new Date().toISOString()}.json`;
      link.click();
      
      toast.success('Data exported successfully!');
    } catch (error) {
      toast.error('Failed to export data');
    } finally {
      setLoading(false);
    }
  };

  const handleRequestDeletion = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/compliance/data-deletion`, {
        user_id: user.id,
        confirmation: true,
        reason: 'User requested account deletion'
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Deletion request submitted. Your data will be deleted in 30 days. You can cancel this request within this period.');
    } catch (error) {
      toast.error('Failed to request deletion');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-gray-50 to-zinc-100">
      <header className="bg-white/80 backdrop-blur-md border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-gradient-to-br from-slate-600 to-gray-700 rounded-xl flex items-center justify-center">
                <Shield className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-gray-900" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Privacy & Data Management</h1>
                <p className="text-sm text-gray-600">Manage your privacy and data</p>
              </div>
            </div>
            <Button onClick={onBack} variant="outline">
              Back to Dashboard
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Tabs defaultValue="settings" className="w-full">
          <TabsList className="grid w-full grid-cols-4 mb-6">
            <TabsTrigger value="settings" className="flex items-center gap-2">
              <Lock className="w-4 h-4" />
              Settings
            </TabsTrigger>
            <TabsTrigger value="consents" className="flex items-center gap-2">
              <FileText className="w-4 h-4" />
              Consents
            </TabsTrigger>
            <TabsTrigger value="data" className="flex items-center gap-2">
              <Download className="w-4 h-4" />
              Your Data
            </TabsTrigger>
            <TabsTrigger value="audit" className="flex items-center gap-2">
              <Eye className="w-4 h-4" />
              Activity Log
            </TabsTrigger>
          </TabsList>

          <TabsContent value="settings" className="space-y-6">
            <Card className="p-6 bg-white/90 backdrop-blur-sm border-0 shadow-lg">
              <h3 className="text-lg font-semibold mb-4">Privacy Preferences</h3>
              <div className="space-y-4">
                <div className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
                  <div className="flex-1">
                    <Label htmlFor="data_collection" className="font-medium">Data Collection</Label>
                    <p className="text-sm text-gray-600">Allow collection of usage data to improve your experience</p>
                  </div>
                  <Switch
                    id="data_collection"
                    checked={privacySettings.data_collection}
                    onCheckedChange={(checked) => handleUpdatePrivacySettings('data_collection', checked)}
                  />
                </div>

                <div className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
                  <div className="flex-1">
                    <Label htmlFor="personalization" className="font-medium">Personalization</Label>
                    <p className="text-sm text-gray-600">Personalize content and recommendations based on your activity</p>
                  </div>
                  <Switch
                    id="personalization"
                    checked={privacySettings.personalization}
                    onCheckedChange={(checked) => handleUpdatePrivacySettings('personalization', checked)}
                  />
                </div>

                <div className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
                  <div className="flex-1">
                    <Label htmlFor="analytics" className="font-medium">Analytics</Label>
                    <p className="text-sm text-gray-600">Help us understand how you use the platform</p>
                  </div>
                  <Switch
                    id="analytics"
                    checked={privacySettings.analytics}
                    onCheckedChange={(checked) => handleUpdatePrivacySettings('analytics', checked)}
                  />
                </div>

                <div className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
                  <div className="flex-1">
                    <Label htmlFor="third_party" className="font-medium">Third-Party Sharing</Label>
                    <p className="text-sm text-gray-600">Share anonymous data with educational partners</p>
                  </div>
                  <Switch
                    id="third_party"
                    checked={privacySettings.third_party_sharing}
                    onCheckedChange={(checked) => handleUpdatePrivacySettings('third_party_sharing', checked)}
                  />
                </div>

                <div className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
                  <div className="flex-1">
                    <Label htmlFor="marketing" className="font-medium">Marketing Emails</Label>
                    <p className="text-sm text-gray-600">Receive updates and promotional emails</p>
                  </div>
                  <Switch
                    id="marketing"
                    checked={privacySettings.marketing_emails}
                    onCheckedChange={(checked) => handleUpdatePrivacySettings('marketing_emails', checked)}
                  />
                </div>
              </div>
            </Card>

            <Card className="p-6 bg-blue-50 border-blue-200">
              <h4 className="font-semibold text-blue-900 mb-2">🔒 Your Privacy Rights</h4>
              <ul className="text-sm text-blue-800 space-y-1">
                <li>• <strong>COPPA Compliance:</strong> We protect children's privacy in accordance with the Children's Online Privacy Protection Act</li>
                <li>• <strong>GDPR Rights:</strong> You have the right to access, rectify, erase, and port your personal data</li>
                <li>• <strong>APPs Compliance:</strong> We follow Australian Privacy Principles for data handling and protection</li>
              </ul>
            </Card>
          </TabsContent>

          <TabsContent value="consents" className="space-y-6">
            <Card className="p-6 bg-white/90 backdrop-blur-sm border-0 shadow-lg">
              <h3 className="text-lg font-semibold mb-4">Your Consent History</h3>
              {consents.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <FileText className="w-12 h-12 mx-auto mb-4 text-gray-400" />
                  <p>No consent records found</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {consents.map((consent, idx) => (
                    <Card key={idx} className="p-4 border border-gray-200">
                      <div className="flex justify-between items-start">
                        <div>
                          <p className="font-medium text-gray-900 capitalize">{consent.consent_type.replace(/_/g, ' ')}</p>
                          <p className="text-sm text-gray-600">
                            {new Date(consent.consent_date).toLocaleString()}
                          </p>
                        </div>
                        <Badge className={consent.consent_given ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}>
                          {consent.consent_given ? 'Granted' : 'Denied'}
                        </Badge>
                      </div>
                    </Card>
                  ))}
                </div>
              )}
            </Card>
          </TabsContent>

          <TabsContent value="data" className="space-y-6">
            <Card className="p-6 bg-white/90 backdrop-blur-sm border-0 shadow-lg">
              <h3 className="text-lg font-semibold mb-4">Data Rights</h3>
              <div className="space-y-4">
                <div className="p-4 border border-gray-200 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <div>
                      <h4 className="font-medium">Export Your Data</h4>
                      <p className="text-sm text-gray-600">Download a copy of all your data (GDPR Right to Portability)</p>
                    </div>
                    <Button onClick={handleExportData} disabled={loading}>
                      <Download className="w-4 h-4 mr-2" />
                      Export
                    </Button>
                  </div>
                </div>

                <div className="p-4 border border-red-200 rounded-lg bg-red-50">
                  <div className="flex items-center justify-between mb-2">
                    <div>
                      <h4 className="font-medium text-red-900">Delete Your Account</h4>
                      <p className="text-sm text-red-700">Permanently delete your account and all associated data</p>
                      <p className="text-xs text-red-600 mt-1">⚠️ This action initiates a 30-day deletion process</p>
                    </div>
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button variant="destructive">
                          <Trash2 className="w-4 h-4 mr-2" />
                          Delete Account
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Delete Your Account?</AlertDialogTitle>
                          <AlertDialogDescription>
                            This will permanently delete your account and all associated data including:
                            <ul className="list-disc list-inside mt-2 space-y-1">
                              <li>Profile information</li>
                              <li>Quiz results and progress</li>
                              <li>Chat history</li>
                              <li>Achievements and badges</li>
                            </ul>
                            <p className="mt-2 font-semibold">You will have 30 days to cancel this request before deletion is final.</p>
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancel</AlertDialogCancel>
                          <AlertDialogAction
                            onClick={handleRequestDeletion}
                            className="bg-red-600 hover:bg-red-700"
                          >
                            Request Deletion
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                </div>
              </div>
            </Card>

            {exportData && (
              <Card className="p-6 bg-green-50 border-green-200">
                <h4 className="font-semibold text-green-900 mb-2">✅ Data Export Complete</h4>
                <p className="text-sm text-green-800">
                  Your data has been exported and downloaded. The file contains all your personal information,
                  quiz results, progress, and activity history in JSON format.
                </p>
              </Card>
            )}
          </TabsContent>

          <TabsContent value="audit" className="space-y-6">
            <Card className="p-6 bg-white/90 backdrop-blur-sm border-0 shadow-lg">
              <h3 className="text-lg font-semibold mb-4">Activity Audit Log</h3>
              <p className="text-sm text-gray-600 mb-4">
                View a history of significant actions on your account
              </p>
              {auditLogs.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <Eye className="w-12 h-12 mx-auto mb-4 text-gray-400" />
                  <p>No activity logs available</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {auditLogs.slice(0, 20).map((log, idx) => (
                    <Card key={idx} className="p-4 border border-gray-200">
                      <div className="flex justify-between items-start">
                        <div>
                          <p className="font-medium text-gray-900">{log.action}</p>
                          <p className="text-sm text-gray-600">{log.description}</p>
                          <p className="text-xs text-gray-500 mt-1">
                            {new Date(log.timestamp).toLocaleString()}
                          </p>
                        </div>
                        <Badge variant="outline">{log.category}</Badge>
                      </div>
                    </Card>
                  ))}
                </div>
              )}
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default PrivacySettings;
